import type { ReactElement } from 'react';
import { Eye } from 'lucide-react';
import { useToast } from '@xdocs/ui';
import { useAdminT } from './useAdminT.js';
import { useImpersonation } from './ImpersonationContext.js';
import styles from './ImpersonationBanner.module.css';

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * F58 persistent, non-dismissible-except-via-Exit view-as banner
 * (§15.1/CLAUDE_DESIGN.md): full-width, sticky, names the impersonated user,
 * shows the hard-TTL countdown, one "Exit view-as" action. Renders nothing
 * while no session is active — mount it once, app-wide, above the routed
 * content (see `WIRING.md`).
 */
export function ImpersonationBanner(): ReactElement | null {
  const { t } = useAdminT();
  const toast = useToast();
  const { session, secondsRemaining, isEnding, end } = useImpersonation();

  if (!session) return null;

  async function handleExit(): Promise<void> {
    try {
      await end();
      toast.push({ tone: 'info', message: t('admin:impersonation.toast.exited') });
    } catch {
      // `error` is available via `useImpersonation()` for a caller that
      // wants to surface it inline; the banner itself stays up so the user
      // isn't left thinking they exited when the server rejected the end.
    }
  }

  return (
    <div className={styles['banner']} role="status" aria-live="polite">
      <span className={styles['label']}>
        <Eye size={15} aria-hidden="true" />
        {t('admin:impersonation.banner.viewingAs', { name: session.targetName })}
      </span>
      <span className={styles['countdown']}>{t('admin:impersonation.banner.endsIn', { time: formatCountdown(secondsRemaining) })}</span>
      <button type="button" className={styles['exitButton']} onClick={() => void handleExit()} disabled={isEnding}>
        {t('admin:impersonation.banner.exit')}
      </button>
    </div>
  );
}
